import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from "../services/apiService";
import * as Flags from 'react-flags-select';
import io from 'socket.io-client';
import config from '../config';
import StreamingFrameStats from '../Components/StreamingFrameStats';
import useStreamingStats from '../hooks/useStreamingStats';
import { snookerPointsRemaining } from '../utils/snookerScoring';
import { normalizePlayerNameSize } from '../utils/streamingTypography';
import '../assets/css/streaming.css';

// One set of fallbacks for the initial state and every fetch path, so an overlay
// that reaches none of the endpoints still goes to air on Style 1 with full names.
const STREAMING_DEFAULTS = { streaming_theme: 'welshopen', display_breaks: 'false', tournament_title: '', player_name_format: 'full', player_name_size: 100, streaming_stats_mode: 'auto' };

// The per-table blob comes back as a JSON string when the column is set, and as an
// object when the API answers with its own defaults. Parsing blindly threw on the
// object form and dropped the whole fetch into its catch, which is why club-level
// settings never reached a table that had no override row.
const parseStreamingSettings = (raw) => {
	if (!raw) return null;
	if (typeof raw === 'object') return raw;
	try {
		return JSON.parse(raw);
	} catch (error) {
		return null;
	}
};

const Streaming = () => {
	const { id: linkId } = useParams();

	// A streaming link is used as an OBS browser source. Mark the document while
	// this route is mounted so the application shells cannot paint their normal
	// dashboard background around the transparent scorebar.
	useEffect(() => {
		const documentRoot = document.documentElement;
		const documentBody = document.body;
		documentRoot.classList.add('streaming-page');
		documentBody.classList.add('streaming-page');

		return () => {
			documentRoot.classList.remove('streaming-page');
			documentBody.classList.remove('streaming-page');
		};
	}, []);

	const [gameState, setGameState] = useState({
		firstPlayer: '',
		secondPlayer: '',
		firstPlayerNationality: 'MA',
		secondPlayerNationality: 'MA',
		player1Score: 0,
		player2Score: 0,
		player1FramesWon: 0,
		player2FramesWon: 0,
		currentPlayer: 1,
		currentBreakPoints: 0,
		maxPoints: 147,
		gameName: '',
		redsRemaining: 15,
		colorsRemaining: { 'Yellow': 2, 'Green': 3, 'Brown': 4, 'Blue': 5, 'Pink': 6, 'Black': 7 },
		colorsPottedFirstTime: { 'Yellow': false, 'Green': false, 'Brown': false, 'Blue': false, 'Pink': false, 'Black': false },
		player1Break: 0,
		player2Break: 0,
		currentBreakPottedBalls: [],
		currentFrame: 1,
		freeBallActive: false,
		streamingStatsCommand: null,
		selectingFoulValue: false,
		frameOver: false,
		lastRedPotted: false,
		firstColorAfterLastRedPotted: false,
		finalColorsPhase: false,
		totalSeconds: 0,
		time: '00 : 00 : 00',
		sequence: 0,
		timestamp: Date.now(),
		isGameFinished: false,
		winMessage: '',
		player1IllegalBreaks: 0,
		player2IllegalBreaks: 0,
		shotTimeRemaining: 45,
		player1ExtensionUsed: false,
		player2ExtensionUsed: false,
		player1PottedBalls: [],
		player2PottedBalls: [],
		shotRemaining: 15000,
		shotTimerIsRunning: false,
		shotStartTimestamp: null,
		timerStartTimestamp: null,
		timerPausedDuration: 0,
		timerIsRunning: false,
	});
	const [players, setPlayers] = useState([]);
	const [game, setGame] = useState({ id: null, id_table: null, first_player_name: '', second_player_name: '', name: '', has_flags: '0', race_to: null, is_race: '0' });
	const [isInitializing, setIsInitializing] = useState(true);
	const [streamingSettings, setStreamingSettings] = useState(STREAMING_DEFAULTS);
	const [gameType, setGameType] = useState('');
	const acceptedSequence = useRef(null);
	const [hasLiveState, setHasLiveState] = useState(false);
	const [maxShotTime, setMaxShotTime] = useState(15000);
	const [currentShotRemaining, setCurrentShotRemaining] = useState(15000);
	const socketRef = useRef(null);
	const showStats = useStreamingStats(gameState, streamingSettings.streaming_stats_mode, hasLiveState && gameType === 'snooker');
	const displayBreaks = !showStats && String(streamingSettings.display_breaks) === 'true';
	const renderFrameStats = () => showStats ? <StreamingFrameStats gameState={gameState} /> : null;

	const getFlagComponent = (countryCode) => {
		if (countryCode && countryCode.length > 1) {
			const formattedCode = countryCode.charAt(0).toUpperCase() + countryCode.slice(1).toLowerCase();
			const FlagComponent = Flags[formattedCode];
			return FlagComponent ? <FlagComponent style={{ width: '24px', height: '16px' }} /> : null;
		}
		return null;
	};

	// Maps the stored game type onto the internal render type. Mini snooker plays as
	// snooker, and us-pool as pool, so both collapse onto the same scoreboard layout.
	const mapGameType = (type) => {
		const t = String(type || '').toLowerCase();
		if (t === 'snooker' || t === 'mini-snooker') return 'snooker';
		if (t === 'chinese-pool') return 'chinese_pool';
		if (t === 'pool' || t === 'us-pool') return 'pool';
		return '';
	};

	const broadcastSurname = (name) => {
		const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
		return parts[parts.length - 1] || '';
	};

	// Full names are the consistent default for every style. Operators can still
	// choose surname-only display when a compact production requires it.
	const displayPlayerName = (name) => {
		const requestedFormat = streamingSettings.player_name_format;
		const resolvedFormat = requestedFormat === 'last' ? 'last' : 'full';
		return resolvedFormat === 'last' ? broadcastSurname(name) : String(name || '').trim();
	};

	// Chinese pool is decided on illegal breaks, so the running count has to be on
	// air the same way it is on the legacy rails: three dots per player, filled as
	// they are conceded. Each rail sizes them through its own `<prefix>-illegals-st`.
	const renderIllegalDots = (prefix, player, count) => (
		<span className={`${prefix}-illegals-st`}>
			{[...Array(3)].map((_, index) => (
				<i key={`${prefix}-${player}-${index}`} className={`illegal-dot-st ${index < count ? 'illegal-on-st' : 'illegal-off-st'}`} />
			))}
		</span>
	);

	// Label shown between the two frame counts. A "first to N" race is played as a
	// best-of, so the total on offer is (N * 2) - 1. Matches that aren't races have
	// no target, so they show a dash instead.
	const raceLabel = (() => {
		const isRace = game && (game.is_race === 1 || game.is_race === '1');
		const target = parseInt(game?.race_to, 10);
		return isRace && Number.isFinite(target) ? `(${target * 2 - 1})` : '-';
	})();

	const themeClass = (() => {
		const t = (streamingSettings.streaming_theme || 'welshopen').toLowerCase();
		if (t === 'default') return 'theme-welshopen-st';
		if (t === 'modern') return 'theme-modern-st';
		if (t === 'neon' || t === 'classic-neon' || t === 'legacy-neon') return 'theme-neon-st';
		if (t === 'welshopen') return 'theme-welshopen-st';
		if (t === 'broadcast') return 'theme-broadcast-st';
		if (t === 'saudi-masters') return 'theme-saudi-masters-st';
		if (t === 'yushan-open') return 'theme-yushan-open-st';
		if (t === 'world-grand-prix') return 'theme-world-grand-prix-st';
		if (t === 'yellowtv') return 'theme-welshopen-st';
		return 'theme-default-st';
	})();

	// The rails are measured against a 1920px reference frame, so the whole graphic
	// is scaled by the browser source's own width. CSS cannot work this factor out
	// on its own - scale() needs a unitless number and calc() will not divide one
	// length by another - so it is published here and kept in step with the source.
	useEffect(() => {
		const applyScale = () => {
			document.documentElement.style.setProperty('--st-scale', String(window.innerWidth / 1920));
		};
		applyScale();
		window.addEventListener('resize', applyScale);
		return () => {
			window.removeEventListener('resize', applyScale);
			document.documentElement.style.removeProperty('--st-scale');
		};
	}, []);

	useEffect(() => {
		const getStreamingSettings = async (tableId) => {
			try {
				if (tableId) {
					const tableData = await apiService.post('/public/streaming-settings', { table_id: tableId });
					const tableSettings = tableData.success ? parseStreamingSettings(tableData.streaming_settings) : null;
					if (tableSettings && (tableSettings.use_streaming_settings === true || tableSettings.use_streaming_settings === 'true')) {
						setStreamingSettings({ ...STREAMING_DEFAULTS, ...tableSettings });
						return;
					}
					// No per-table override, so fall back to what the club saved under
					// Settings -> Streaming. This has to be the public route: the
					// authenticated one answers an OBS browser source with a 401, and
					// apiService turns any 401 into a redirect to /login - which takes the
					// overlay off air and replaces it with the marketing page.
					const clubData = await apiService.post('/public/settings/streaming', { table_id: tableId });
					if (clubData.success && clubData.settings) {
						setStreamingSettings({ ...STREAMING_DEFAULTS, ...clubData.settings });
						return;
					}
				}
				setStreamingSettings(STREAMING_DEFAULTS);
			} catch (error) {
				console.error('Error fetching streaming settings:', error);
				setStreamingSettings(STREAMING_DEFAULTS);
			}
		};
		const getClubPlayers = async () => {
			try {
				const data = await apiService.post('/public/players', { link: linkId });
				setPlayers(data.players || []);
			} catch (error) {
				console.error('Error fetching club players:', error);
				setPlayers([]);
			}
		};
		const getGame = async () => {
			try {
				const data = await apiService.post('/public/game-link', { link: linkId });
				const gameData = (data.is_linked_game && data.linked_game) ? data.linked_game : data.game;
				setGame(gameData || { id: null, id_table: null, first_player_name: '', second_player_name: '', name: '', has_flags: '0', race_to: null, is_race: '0' });
				setGameState(prev => ({
					...prev,
					firstPlayer: gameData?.first_player_name || '',
					secondPlayer: gameData?.second_player_name || '',
					gameName: gameData?.name || '',
				}));
				// Seed the render type from the game record so the scoreboard is complete on
				// first paint. Without this it stays blank until the first socket update
				// arrives, which only happens once the referee acts.
				setGameType(prev => prev || mapGameType(gameData?.type));
				await getStreamingSettings(gameData?.id_table);
			} catch (error) {
				console.error('Error fetching game:', error);
				setGame({ id: null, id_table: null, first_player_name: '', second_player_name: '', name: '', has_flags: '0', race_to: null, is_race: '0' });
			}
		};
		const init = async () => {
			setIsInitializing(true);
			try {
				await getGame();
				await getClubPlayers();
			} catch (error) {
				console.error('Initialization error:', error);
			} finally {
				setIsInitializing(false);
			}
		};
		init();
	}, [linkId]);

	useEffect(() => {
		const syncStateFromSocket = (incomingState) => {
			const sequence = Number(incomingState.sequence);
			if (!Number.isFinite(sequence)) return;
			if (acceptedSequence.current === null || sequence > acceptedSequence.current) {
				acceptedSequence.current = sequence;
				setHasLiveState(true);
				const type = incomingState.reds_remaining !== undefined ? 'snooker' : incomingState.player1_illegal_breaks !== undefined ? 'chinese_pool' : 'pool';
				setGameType(type);
				setGameState(prev => ({
					...prev,
					firstPlayer: (incomingState.first_player && incomingState.first_player !== 'PLAYER A') ? incomingState.first_player : prev.firstPlayer,
					secondPlayer: (incomingState.second_player && incomingState.second_player !== 'PLAYER B') ? incomingState.second_player : prev.secondPlayer,
					currentPlayer: incomingState.current_player !== undefined ? incomingState.current_player : prev.currentPlayer,
					player1Score: incomingState.first_player_score !== undefined ? incomingState.first_player_score : prev.player1Score,
					player1FramesWon: incomingState.first_player_frames !== undefined ? incomingState.first_player_frames : prev.player1FramesWon,
					player2Score: incomingState.second_player_score !== undefined ? incomingState.second_player_score : prev.player2Score,
					player2FramesWon: incomingState.second_player_frames !== undefined ? incomingState.second_player_frames : prev.player2FramesWon,
					currentBreakPoints: incomingState.current_break_points !== undefined ? incomingState.current_break_points : prev.currentBreakPoints,
					maxPoints: incomingState.max_points !== undefined ? incomingState.max_points : prev.maxPoints,
					redsRemaining: incomingState.reds_remaining !== undefined ? incomingState.reds_remaining : prev.redsRemaining,
					colorsRemaining: incomingState.colors_remaining || prev.colorsRemaining,
					colorsPottedFirstTime: incomingState.colors_potted_first_time || prev.colorsPottedFirstTime,
					player1Break: incomingState.first_player_break !== undefined ? incomingState.first_player_break : prev.player1Break,
					player2Break: incomingState.second_player_break !== undefined ? incomingState.second_player_break : prev.player2Break,
					currentBreakPottedBalls: incomingState.current_break_potted_balls || prev.currentBreakPottedBalls,
					currentFrame: incomingState.current_frame !== undefined ? incomingState.current_frame : prev.currentFrame,
					totalSeconds: incomingState.total_seconds !== undefined ? incomingState.total_seconds : prev.totalSeconds,
					freeBallActive: incomingState.free_ball_active !== undefined ? incomingState.free_ball_active : prev.freeBallActive,
					streamingStatsCommand: incomingState.streaming_stats_command !== undefined ? incomingState.streaming_stats_command : prev.streamingStatsCommand,
					selectingFoulValue: incomingState.selecting_foul_value !== undefined ? incomingState.selecting_foul_value : prev.selectingFoulValue,
					frameOver: incomingState.frame_over !== undefined ? incomingState.frame_over : prev.frameOver,
					lastRedPotted: incomingState.last_red_potted !== undefined ? incomingState.last_red_potted : prev.lastRedPotted,
					firstColorAfterLastRedPotted: incomingState.first_color_after_last_red_potted !== undefined ? incomingState.first_color_after_last_red_potted : prev.firstColorAfterLastRedPotted,
					finalColorsPhase: incomingState.final_colors_phase !== undefined ? incomingState.final_colors_phase : prev.finalColorsPhase,
					sequence: incomingState.sequence,
					timestamp: incomingState.timestamp,
					isGameFinished: incomingState.is_game_finished !== undefined ? incomingState.is_game_finished : prev.isGameFinished,
					winMessage: incomingState.win_message !== undefined ? incomingState.win_message : prev.winMessage,
					player1IllegalBreaks: incomingState.player1_illegal_breaks !== undefined ? incomingState.player1_illegal_breaks : prev.player1IllegalBreaks,
					player2IllegalBreaks: incomingState.player2_illegal_breaks !== undefined ? incomingState.player2_illegal_breaks : prev.player2IllegalBreaks,
					shotTimeRemaining: incomingState.shot_time_remaining !== undefined ? incomingState.shot_time_remaining : prev.shotTimeRemaining,
					player1ExtensionUsed: incomingState.player1_extension_used !== undefined ? incomingState.player1_extension_used : prev.player1ExtensionUsed,
					player2ExtensionUsed: incomingState.player2_extension_used !== undefined ? incomingState.player2_extension_used : prev.player2ExtensionUsed,
					player1PottedBalls: incomingState.player1_potted_balls || prev.player1PottedBalls,
					player2PottedBalls: incomingState.player2_potted_balls || prev.player2PottedBalls,
					shotRemaining: incomingState.shot_remaining !== undefined ? incomingState.shot_remaining : prev.shotRemaining,
					shotTimerIsRunning: incomingState.shot_timer_is_running !== undefined ? incomingState.shot_timer_is_running : prev.shotTimerIsRunning,
					shotStartTimestamp: incomingState.shot_start_timestamp !== undefined ? incomingState.shot_start_timestamp : prev.shotStartTimestamp,
					timerStartTimestamp: incomingState.timer_start_timestamp !== undefined ? incomingState.timer_start_timestamp : prev.timerStartTimestamp,
					timerPausedDuration: incomingState.timer_paused_duration !== undefined ? incomingState.timer_paused_duration : prev.timerPausedDuration,
					timerIsRunning: incomingState.timer_is_running !== undefined ? incomingState.timer_is_running : prev.timerIsRunning,
				}));
			}
		};
		if (!game?.id) return;
		acceptedSequence.current = null;
		setHasLiveState(false);
		if (!socketRef.current) {
			socketRef.current = io(config.SOCKET_URL, {
				transports: ['polling', 'websocket'],
				timeout: 20000,
				forceNew: true,
				reconnection: true,
				reconnectionDelay: 1000,
				reconnectionAttempts: Infinity,
			});
			socketRef.current.on('connect', () => {
				socketRef.current.emit('joinGame', game.id);
				if (game.id_table) {
					socketRef.current.emit('joinTableRoom', game.id_table);
				}
				socketRef.current.emit('requestCurrentState');
			});
			socketRef.current.on('connect_error', (error) => {
				console.error('Socket connect error:', error);
			});
			socketRef.current.on('disconnect', (reason) => {
				console.log('Socket disconnected:', reason);
			});
			socketRef.current.on('gameStateUpdate', (incomingState) => {
				if (incomingState.id === game.id) {
					setIsInitializing(false);
					syncStateFromSocket(incomingState);
				}
			});
			socketRef.current.on('initialGameState', (incomingState) => {
				if (incomingState && incomingState.id === game.id) {
					setIsInitializing(false);
					syncStateFromSocket(incomingState);
				} else {
					setIsInitializing(false);
				}
			});
			socketRef.current.on('tableGameLinked', () => {
				console.log('[Streaming] tableGameLinked — reloading');
				try { window.location.reload(); } catch (e) {}
			});
			// Also reload on refreshGame (emitted to the game room we're already in)
			socketRef.current.on('refreshGame', () => {
				console.log('[Streaming] refreshGame — reloading');
				try { window.location.reload(); } catch (e) {}
			});
		}
		return () => {
			if (socketRef.current) {
				if (game?.id) socketRef.current.emit('leaveGame', game.id);
				socketRef.current.disconnect();
				socketRef.current = null;
			}
		};
	}, [game?.id, game?.id_table]);

	useEffect(() => {
		if (game && players.length > 0) {
			const firstPlayer = players.find(p => p.id === game.first_player);
			const secondPlayer = players.find(p => p.id === game.second_player);
			setGameState(prev => ({
				...prev,
				firstPlayerNationality: firstPlayer?.nationality || prev.firstPlayerNationality,
				secondPlayerNationality: secondPlayer?.nationality || prev.secondPlayerNationality,
			}));
		}
	}, [players, game]);

	useEffect(() => {
		if (game && game.is_race === '1' && !gameState.isGameFinished) {
			if (parseInt(game.race_to) <= gameState.player1FramesWon) {
				setGameState(prev => ({ ...prev, isGameFinished: true, winMessage: `${prev.firstPlayer} won the game!` }));
			} else if (parseInt(game.race_to) <= gameState.player2FramesWon) {
				setGameState(prev => ({ ...prev, isGameFinished: true, winMessage: `${prev.secondPlayer} won the game!` }));
			}
		}
	}, [game, gameState.player1FramesWon, gameState.player2FramesWon, gameState.isGameFinished]);

	useEffect(() => {
		const prevRunning = gameState.shotTimerIsRunning;
		return () => {
			if (!prevRunning && gameState.shotTimerIsRunning) {
				setMaxShotTime(gameState.shotRemaining);
			}
		};
	}, [gameState.shotTimerIsRunning]);

	useEffect(() => {
		let interval;
		if (gameType === 'snooker' && gameState.shotTimerIsRunning) {
			interval = setInterval(() => {
				if (gameState.shotTimerIsRunning && gameState.shotStartTimestamp) {
					const elapsed = Date.now() - gameState.shotStartTimestamp;
					const remaining = Math.max(0, gameState.shotRemaining - elapsed);
					setCurrentShotRemaining(remaining);
				}
			}, 100);
		}
		return () => clearInterval(interval);
	}, [gameState.shotTimerIsRunning, gameState.shotStartTimestamp, gameState.shotRemaining, gameType]);

	/* Helpers */
	const renderBallsBreak = (player, ignoreBreakSetting = false) => {
		if (
			(!ignoreBreakSetting && !displayBreaks) ||
			gameType !== 'snooker' ||
			gameState.currentBreakPoints <= 0 ||
			gameState.currentBreakPottedBalls.length <= 0 ||
			gameState.currentPlayer !== player
		) return null;

		const colorClass = (c) => {
			const k = c.toLowerCase();
			if (k === 'red') return 'break-red-st';
			if (k === 'yellow') return 'break-yellow-st';
			if (k === 'green') return 'break-green-st';
			if (k === 'brown') return 'break-brown-st';
			if (k === 'blue') return 'break-blue-st';
			if (k === 'pink') return 'break-pink-st';
			return 'break-black-st';
		};

		return (
			<div className="break-balls-st">
				{['red', 'yellow', 'green', 'brown', 'blue', 'pink', 'black'].map((color, index) => {
					const count = gameState.currentBreakPottedBalls.filter(b => b.toLowerCase() === color).length;
					if (count === 0) return null;
					return (
						<div key={`break-${index}`} className={`break-ball-st ${colorClass(color)}`}>
							{count}
						</div>
					);
				})}
			</div>
		);
	};

	/* ===== THEMES ===== */

	const renderDefaultTheme = () => {
		const secondsLeft = Math.ceil(currentShotRemaining / 1000);
		const percentage = (currentShotRemaining / maxShotTime) * 100;
		const timerState = secondsLeft <= 5 ? 'timer-danger-st' : 'timer-ok-st';
		const hasFlags = game && game.has_flags !== '0';

		// country codes must be lowercase for flagcdn
		const cc1 = String(gameState.firstPlayerNationality || '').toLowerCase();
		const cc2 = String(gameState.secondPlayerNationality || '').toLowerCase();

		return (
			<div className={`streaming-root-st ${themeClass}`}>
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st">
						<p className="streaming-win-text-st">{gameState.winMessage}</p>
					</div>
				)}

				<div className="topbar-ctn-st">
					{renderFrameStats()}
					<div className="header-bar-st">
						{/* left player (name + timer) */}
						<div className="player-block-st player-left-st">
							<div className="row-st" style={{ gap: '0.4vw', justifyContent: 'flex-start' }}>
								<span className="player-name-st">{gameState.firstPlayer}</span>
								{gameType === 'snooker' && gameState.shotTimerIsRunning && gameState.currentPlayer === 1 && (
									<div className={`timer-pill-st ${timerState}`} style={{ '--pct': `${percentage}%` }}>
										<div className="timer-pill-fill-st" />
										<div className="timer-pill-bg-st">{secondsLeft}s</div>
									</div>
								)}
							</div>
							{gameType === 'chinese_pool' && (
								<div className="row-st" style={{ gap: 5, marginLeft: 10 }}>
									{[...Array(3)].map((_, index) => (
										<span
											key={`p1-${index}`}
											className={`illegal-dot-st ${index < gameState.player1IllegalBreaks ? 'illegal-on-st' : 'illegal-off-st'}`}
										/>
									))}
								</div>
							)}
							{gameState.currentPlayer === 1 && <span className="pointer-left-st" aria-hidden="true" />}
						</div>

						{/* center strip: flags + scores + frames */}
						<div className="row-center-st default-center-st">
							{/* LEFT score group: FLAG — SCORE */}
							{gameType === 'snooker' && (
								<div className="score-group-st">
									{hasFlags && cc1 && (
										<div className="flag-box-st">
											<img
												className="flag-img-st"
												src={`https://flagcdn.com/${cc1}.svg`}
												alt={gameState.firstPlayerNationality}
												loading="eager"
											/>
										</div>
									)}
									<div className="score-val-st default-score-st">{gameState.player1Score}</div>
								</div>
							)}

							{/* frames */}
							<div className="frames-box-st">
								<span className="frames-val-st">{gameState.player1FramesWon}</span>
								<span className="frame-number-st">{raceLabel}</span>
								<span className="frames-val-st">{gameState.player2FramesWon}</span>
							</div>

							{/* RIGHT score group: SCORE — FLAG */}
							{gameType === 'snooker' && (
								<div className="score-group-st">
									<div className="score-val-st default-score-st">{gameState.player2Score}</div>
									{hasFlags && cc2 && (
										<div className="flag-box-st">
											<img
												className="flag-img-st"
												src={`https://flagcdn.com/${cc2}.svg`}
												alt={gameState.secondPlayerNationality}
												loading="eager"
											/>
										</div>
									)}
								</div>
							)}
						</div>

						{/* right player (name + timer) */}
						<div className="player-block-st player-right-st">
							<div className="row-st" style={{ gap: '0.4vw', justifyContent: 'flex-end' }}>
								{gameType === 'snooker' && gameState.shotTimerIsRunning && gameState.currentPlayer === 2 && (
									<div className={`timer-pill-st ${timerState}`} style={{ '--pct': `${percentage}%` }}>
										<div className="timer-pill-fill-st" />
										<div className="timer-pill-bg-st">{secondsLeft}s</div>
									</div>
								)}
								<span className="player-name-st">{gameState.secondPlayer}</span>
							</div>
							{gameType === 'chinese_pool' && (
								<div className="row-st" style={{ gap: 5, marginRight: 10 }}>
									{[...Array(3)].map((_, index) => (
										<span
											key={`p2-${index}`}
											className={`illegal-dot-st ${index < gameState.player2IllegalBreaks ? 'illegal-on-st' : 'illegal-off-st'}`}
										/>
									))}
								</div>
							)}
							{gameState.currentPlayer === 2 && <span className="pointer-right-st" aria-hidden="true" />}
						</div>
					</div>

					{/* subbar */}
					<div className="subbar-ctn-st row-center-st">
						{displayBreaks &&
							gameType === 'snooker' &&
							gameState.currentBreakPoints > 0 &&
							gameState.currentBreakPottedBalls.length > 0 &&
							gameState.currentPlayer === 1 && (
								<div className="break-panel-st break-left-st">
									<span className="break-label-st">Break</span>
									<div className="row-st" style={{ gap: 18 }}>
										{renderBallsBreak(1)}
										{gameState.player1Break > 0 && (
											<div style={{ borderRadius: 3, fontSize: 20, color: 'white', fontWeight: 'bold' }}>
												{gameState.player1Break}
											</div>
										)}
									</div>
								</div>
							)}

						<span className="tournament-badge-st">
							<span className="tournament-badge-text-st">
								{streamingSettings.tournament_title && streamingSettings.tournament_title.trim() !== ''
									? streamingSettings.tournament_title
									: gameType === 'snooker'
										? gameState.maxPoints
										: ''}
							</span>
						</span>

						{displayBreaks &&
							gameType === 'snooker' &&
							gameState.currentBreakPoints > 0 &&
							gameState.currentBreakPottedBalls.length > 0 &&
							gameState.currentPlayer === 2 && (
								<div className="break-panel-st break-right-st">
									<span className="break-label-st">Break</span>
									<div className="row-st" style={{ gap: 18 }}>
										{renderBallsBreak(2)}
										{gameState.player2Break > 0 && (
											<div style={{ borderRadius: 3, fontSize: 20, color: 'white', fontWeight: 'bold' }}>
												{gameState.player2Break}
											</div>
										)}
									</div>
								</div>
							)}
					</div>
				</div>
			</div>
		);
	};



	const renderModernTheme = () => {
		const secondsLeft = Math.ceil(currentShotRemaining / 1000);
		const percentage = (currentShotRemaining / maxShotTime) * 100;
		const timerState = secondsLeft <= 5 ? 'timer-danger-st' : 'timer-ok-st';

		return (
			<div className={`streaming-root-st ${themeClass}`}>
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st">
						<p className="streaming-win-text-st">{gameState.winMessage}</p>
					</div>
				)}

				<div className="modern-topbar-st">
					{renderFrameStats()}
					<div className="modern-head-st">
						{/* left chip */}
						<div className={`modern-chip-st ${gameState.currentPlayer === 1 ? 'modern-chip-active-st' : 'modern-chip-idle-st'}`}>
							{game && game.has_flags !== '0' && <div className="flag-box-st" style={{ border: '2px solid rgba(255,255,255,0.4)', borderRadius: '50%', padding: 2 }}>{getFlagComponent(gameState.firstPlayerNationality)}</div>}
							{displayBreaks && gameType === 'snooker' && gameState.currentPlayer === 1 && gameState.player1Break > 0 && (
								<div style={{ background: 'var(--accent)', color: '#000', padding: '4px 8px', borderRadius: 15, fontSize: 12, fontWeight: 'bold' }}>{gameState.player1Break}</div>
							)}
							<div className="row-st" style={{ gap: '0.4vw', justifyContent: 'flex-start' }}>
								<span className="player-name-st" style={{ fontSize: 'calc(16px * var(--st-name-scale, 1))', fontWeight: 600, letterSpacing: 1 }}>{gameState.firstPlayer}</span>
								{gameType === 'snooker' && gameState.shotTimerIsRunning && gameState.currentPlayer === 1 && (
									<div className={`timer-pill-st ${timerState}`} style={{ '--pct': `${percentage}%` }}>
										<div className="timer-pill-fill-st" />
										<div className="timer-pill-bg-st">{secondsLeft}s</div>
									</div>
								)}
							</div>
							{gameType === 'chinese_pool' && (
								<div className="row-st" style={{ gap: 5, marginLeft: 10 }}>
									{[...Array(3)].map((_, index) => (
										<span key={`p1-${index}`} className={`illegal-dot-st ${index < gameState.player1IllegalBreaks ? 'illegal-on-st' : 'illegal-off-st'}`} />
									))}
								</div>
							)}
						</div>

						{/* center */}
						<div className="modern-center-st">
							{gameType === 'snooker' && <div className="score-val-st" style={{ fontSize: 36 }}>{gameState.player1Score}</div>}

							<div className="modern-frames-st">
								<span className="modern-frames-num-st">{gameState.player1FramesWon}</span>
								<span className="modern-frames-cur-st">{raceLabel}</span>
								<span className="modern-frames-num-st">{gameState.player2FramesWon}</span>
							</div>

							{gameType === 'snooker' && <div className="score-val-st" style={{ fontSize: 36 }}>{gameState.player2Score}</div>}
						</div>

						{/* right chip */}
						<div className={`modern-chip-st ${gameState.currentPlayer === 2 ? 'modern-chip-active-st' : 'modern-chip-idle-st'}`} style={{ marginLeft: 'auto' }}>
							{displayBreaks && gameType === 'snooker' && gameState.currentPlayer === 2 && gameState.player2Break > 0 && (
								<div style={{ background: 'var(--accent)', color: '#000', padding: '4px 8px', borderRadius: 15, fontSize: 12, fontWeight: 'bold' }}>{gameState.player2Break}</div>
							)}
							{gameType === 'chinese_pool' && (
								<div className="row-st" style={{ gap: 5, marginRight: 10 }}>
									{[...Array(3)].map((_, index) => (
										<span key={`p2-${index}`} className={`illegal-dot-st ${index < gameState.player2IllegalBreaks ? 'illegal-on-st' : 'illegal-off-st'}`} />
									))}
								</div>
							)}
							<div className="row-st" style={{ gap: '0.4vw', justifyContent: 'flex-end' }}>
								{gameType === 'snooker' && gameState.shotTimerIsRunning && gameState.currentPlayer === 2 && (
									<div className={`timer-pill-st ${timerState}`} style={{ '--pct': `${percentage}%` }}>
										<div className="timer-pill-fill-st" />
										<div className="timer-pill-bg-st">{secondsLeft}s</div>
									</div>
								)}
								<span className="player-name-st" style={{ fontSize: 'calc(16px * var(--st-name-scale, 1))', fontWeight: 600, letterSpacing: 1 }}>{gameState.secondPlayer}</span>
							</div>
							{game && game.has_flags !== '0' && <div className="flag-box-st" style={{ border: '2px solid rgba(255,255,255,0.4)', borderRadius: '50%', padding: 2 }}>{getFlagComponent(gameState.secondPlayerNationality)}</div>}
						</div>
					</div>

					{/* lower strip */}
					<div className="modern-lower-st">
						{displayBreaks && gameType === 'snooker' && gameState.currentBreakPoints > 0 && gameState.currentBreakPottedBalls.length > 0 && gameState.currentPlayer === 1 && (
							<div className="row-st" style={{ position: 'absolute', left: 25, gap: 8, height: '100%', paddingRight: 20, background: 'rgba(0,212,255,0.2)', borderRadius: 20, paddingLeft: 20, backdropFilter: 'blur(10px)' }}>
								<span className="player-name-st" style={{ fontSize: 16, fontWeight: 600, letterSpacing: .5 }}>Current Break:</span>
								{renderBallsBreak(1)}
							</div>
						)}

						<span className="modern-center-badge-st">
							{streamingSettings.tournament_title && streamingSettings.tournament_title.trim() !== '' ? streamingSettings.tournament_title : gameType === 'snooker' ? gameState.maxPoints : ''}
						</span>

						{displayBreaks && gameType === 'snooker' && gameState.currentBreakPoints > 0 && gameState.currentBreakPottedBalls.length > 0 && gameState.currentPlayer === 2 && (
							<div className="row-st" style={{ position: 'absolute', right: 25, gap: 8, height: '100%', paddingLeft: 20, background: 'rgba(0,212,255,0.2)', borderRadius: 20, paddingRight: 20, backdropFilter: 'blur(10px)' }}>
								<span className="player-name-st" style={{ fontSize: 16, fontWeight: 600, letterSpacing: .5 }}>Current Break:</span>
								{renderBallsBreak(2)}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	const renderNeonTheme = () => {
		const hasFlags = game && game.has_flags !== '0';
		const secondsLeft = Math.ceil(currentShotRemaining / 1000);
		const percentage = (currentShotRemaining / maxShotTime) * 100;
		const timerState = secondsLeft <= 5 ? 'timer-danger-st' : 'timer-ok-st';

		return (
			<div className={`streaming-root-st ${themeClass}`}>
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st">
						<p className="streaming-win-text-st">{gameState.winMessage}</p>
					</div>
				)}

				<div style={{ width: '100vw', position: 'fixed', top: 0, left: 0, zIndex: 1000, border: 'none' }}>
					{renderFrameStats()}
					<div className="neon-header-row-st">
						<div className="neon-side-block-st" />
						<div className="neon-name-cell-st">
							{hasFlags && (
								<div className="neon-flag-pic-st">
									<img className="neon-flag-img-st" src={`https://flagcdn.com/w80/${String(gameState.firstPlayerNationality).toLowerCase()}.png`} alt={gameState.firstPlayerNationality} />
								</div>
							)}
							<div className="row-st" style={{ gap: '0.4vw', justifyContent: 'flex-start' }}>
								<span className="neon-name-st">{displayPlayerName(gameState.firstPlayer)}</span>
								{gameType === 'snooker' && gameState.shotTimerIsRunning && gameState.currentPlayer === 1 && (
									<div className={`timer-pill-st ${timerState}`} style={{ '--pct': `${percentage}%` }}>
										<div className="timer-pill-fill-st" />
										<div className="timer-pill-bg-st">{secondsLeft}s</div>
									</div>
								)}
							</div>
							{gameType === 'chinese_pool' && (
								<div className="neon-illegals-st" style={{ marginLeft: '1vw' }}>
									{[...Array(3)].map((_, index) => (
										<span key={`p1-break-${index}`} className={`illegal-dot-st ${index < gameState.player1IllegalBreaks ? 'illegal-on-st' : 'illegal-off-st'}`} style={{ width: '1.2vh', height: '1.2vh' }} />
									))}
								</div>
							)}
							{gameState.currentPlayer === 1 && <div className="neon-arrow-left-st" />}
						</div>

						<div className={`neon-score-strip-st ${gameType === 'snooker' ? '' : 'is-pool-st'}`}>
							{gameType === 'snooker' && <div className="neon-score-cell-st">{gameState.player1Score}</div>}
							<div className="neon-frames-cell-st">
								<span>{gameState.player1FramesWon}</span>
								<span>{raceLabel}</span>
								<span>{gameState.player2FramesWon}</span>
							</div>
							{gameType === 'snooker' && <div className="neon-score-cell-st">{gameState.player2Score}</div>}
						</div>

						<div className="neon-right-name-cell-st">
							{gameState.currentPlayer === 2 && <div className="neon-arrow-right-st" />}
							{gameType === 'chinese_pool' && (
								<div className="neon-illegals-st" style={{ marginRight: '1vw' }}>
									{[...Array(3)].map((_, index) => (
										<span key={`p2-break-${index}`} className={`illegal-dot-st ${index < gameState.player2IllegalBreaks ? 'illegal-on-st' : 'illegal-off-st'}`} style={{ width: '1.2vh', height: '1.2vh' }} />
									))}
								</div>
							)}
							<div className="row-st" style={{ gap: '0.4vw', justifyContent: 'flex-end' }}>
								{gameType === 'snooker' && gameState.shotTimerIsRunning && gameState.currentPlayer === 2 && (
									<div className={`timer-pill-st ${timerState}`} style={{ '--pct': `${percentage}%` }}>
										<div className="timer-pill-fill-st" />
										<div className="timer-pill-bg-st">{secondsLeft}s</div>
									</div>
								)}
								<span className="neon-name-st" style={{ textAlign: hasFlags ? 'right' : 'center' }}>{displayPlayerName(gameState.secondPlayer)}</span>
							</div>
							{hasFlags && (
								<div className="neon-flag-pic-st">
									<img className="neon-flag-img-st" src={`https://flagcdn.com/w80/${String(gameState.secondPlayerNationality).toLowerCase()}.png`} alt={gameState.secondPlayerNationality} />
								</div>
							)}
						</div>
						<div className="neon-side-block-st" />
					</div>

					{/* The grey information rail is part of the snooker graphic. Pool
					    variants do not have remaining-points information, so an empty rail
					    should not consume valuable picture area. */}
					{gameType === 'snooker' && <div className="neon-lower-st">
						<div className="neon-lower-spacer-st" />
						<div className="neon-lower-col-st">
							<div className="row-st" style={{ justifyContent: 'flex-end', alignItems: 'center', paddingRight: '2vw', width: '100%' }}>
								{displayBreaks && gameType === 'snooker' && gameState.currentBreakPoints > 0 && gameState.currentBreakPottedBalls.length > 0 && gameState.currentPlayer === 1 && (
									<div className="neon-break-chip-st">
										<span style={{ fontSize: '2vh', color: '#fff', fontWeight: 900 }}>{gameState.currentBreakPoints} Break</span>
										{renderBallsBreak(1)}
									</div>
								)}
							</div>
						</div>

						<div className="neon-lower-mid-st">
							<div />
							<div className="neon-remaining-st">
								{gameType === 'snooker' ? `Remaining  ${gameState.maxPoints}` : ''}
							</div>
							<div />
						</div>

						<div className="neon-lower-col-st">
							<div className="row-st" style={{ justifyContent: 'flex-start', alignItems: 'center', paddingLeft: '2vw', width: '100%' }}>
								{displayBreaks && gameType === 'snooker' && gameState.currentBreakPoints > 0 && gameState.currentBreakPottedBalls.length > 0 && gameState.currentPlayer === 2 && (
									<div className="neon-break-chip-st">
										<span style={{ fontSize: '2vh', color: '#fff', fontWeight: 900 }}>{gameState.currentBreakPoints} Break</span>
										{renderBallsBreak(2)}
									</div>
								)}
							</div>
						</div>
						<div className="neon-lower-spacer-st" />
					</div>}
				</div>
			</div>
		);
	};

	const renderWelshOpenTheme = () => {
		const isLeftActive = gameState.currentPlayer === 1;
		const isRightActive = gameState.currentPlayer === 2;

		return (
			<div className={`streaming-root-st ${themeClass}`}>
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st">
						<p className="streaming-win-text-st">{gameState.winMessage}</p>
					</div>
				)}

				{/* Bar stretches; inner chips stay fixed via vw/vh tokens */}
				<div className="wo-bar-st">
					{renderFrameStats()}
					{/* LEFT: name chip + active pointer (► toward name) */}
					<div className="wo-side-st wo-left-st">
						{isLeftActive && <span className="wo-pointer-left-st" aria-hidden="true" />}
						<div className="wo-name-chip-st">
							<span className="wo-name-st">{gameState.firstPlayer}</span>
						</div>
					</div>

					{/* CENTER: left gray | blue | right gray */}
					<div className="wo-center-st">
						<div className="wo-chip left-gray-st">{gameState.player1Score}</div>
						<div className="wo-chip blue-st">
							<span>{gameState.player1FramesWon}</span>
							<span className="wo-tiebreak-st">{raceLabel}</span>
							<span>{gameState.player2FramesWon}</span>
						</div>
						<div className="wo-chip right-gray-st">{gameState.player2Score}</div>
					</div>

					{/* RIGHT: name chip + active pointer (◄ toward name) */}
					<div className="wo-side-st wo-right-st">
						<div className="wo-name-chip-st">
							<span className="wo-name-st">{gameState.secondPlayer}</span>
						</div>
						{isRightActive && <span className="wo-pointer-right-st" aria-hidden="true" />}
					</div>
				</div>
			</div>
		);
	};


	// English Open (Style 5) — the 2025 WST treatment uses a slim charcoal
	// lower-third, pale points cells and an electric-blue frame cell. National flags are
	// deliberately omitted: the reference graphic identifies players by name and
	// uses a small blue triangle as the only active-player accent.
	const renderBroadcastTheme = () => {
		const secondsLeft = Math.ceil(currentShotRemaining / 1000);
		const percentage = (currentShotRemaining / maxShotTime) * 100;
		const timerState = secondsLeft <= 5 ? 'timer-danger-st' : 'timer-ok-st';
		const isSnooker = gameType === 'snooker';
		const title = String(streamingSettings.tournament_title || '').trim();
		const showBreak =
			displayBreaks &&
			isSnooker &&
			gameState.currentBreakPoints > 0 &&
			gameState.currentBreakPottedBalls.length > 0;

		const illegals = (player, count) => (
			<div className="bc-illegals-st">
				{[...Array(3)].map((_, index) => (
					<span
						key={`bc-p${player}-break-${index}`}
						className={`illegal-dot-st ${index < count ? 'illegal-on-st' : 'illegal-off-st'}`}
					/>
				))}
			</div>
		);

		const shotTimer = (player) =>
			isSnooker && gameState.shotTimerIsRunning && gameState.currentPlayer === player ? (
				<div className={`timer-pill-st ${timerState}`} style={{ '--pct': `${percentage}%` }}>
					<div className="timer-pill-fill-st" />
					<div className="timer-pill-bg-st">{secondsLeft}s</div>
				</div>
			) : null;

		return (
			<div className={`streaming-root-st ${themeClass}`}>
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st">
						<p className="streaming-win-text-st">{gameState.winMessage}</p>
					</div>
				)}

				{/* the narrow modifier keeps the tournament strip aligned to the
				    score block when the points cells are hidden for pool games */}
				<div className={`bc-wrap-st ${isSnooker ? '' : 'bc-wrap-narrow-st'}`}>
					{renderFrameStats()}
					{/* Break panel sits directly above the bar on the side of the player
					    at the table, outer edge aligned with the bar's. It is taken out
					    of flow, and the wrapper reserves its height, so a break starting
					    or ending never shifts the bar itself. */}
					{showBreak && (
						<div className={`bc-break-row-st ${gameState.currentPlayer === 1 ? 'bc-break-left-st' : 'bc-break-right-st'}`}>
							<div className="bc-break-st">
								{/* Balls sit against the panel's outer edge and the points against
								    the inner one. A flexible spacer rather than a fixed gap so it
								    collapses as the break picks up more colours - the panel must
								    never grow inward over the score block to fit. On the right the
								    whole row is reversed, so the balls stay on the outside. */}
								{renderBallsBreak(gameState.currentPlayer)}
								<span className="bc-break-gap-st" />
								<span className="bc-break-label-st">
									Break <span className="bc-break-pts-st">{gameState.currentBreakPoints}</span>
								</span>
							</div>
						</div>
					)}

					<div className="bc-bar-st">
						<div className="bc-side-st bc-left-st">
							<span className="bc-name-st">{gameState.firstPlayer}</span>
							{gameType === 'chinese_pool' && illegals(1, gameState.player1IllegalBreaks)}
							{shotTimer(1)}
						</div>

						<div className="bc-arrow-slot-st">
							{gameState.currentPlayer === 1 && <span className="bc-arrow-left-st" />}
						</div>

						<div className="bc-center-st">
							{isSnooker && <div className="bc-cell-st bc-pts-st">{gameState.player1Score}</div>}
							<div className="bc-cell-st bc-frames-st">
								<span className="bc-frame-num-st">{gameState.player1FramesWon}</span>
								<span className="bc-race-st">{raceLabel}</span>
								<span className="bc-frame-num-st">{gameState.player2FramesWon}</span>
							</div>
							{isSnooker && <div className="bc-cell-st bc-pts-st">{gameState.player2Score}</div>}
						</div>

						<div className="bc-arrow-slot-st">
							{gameState.currentPlayer === 2 && <span className="bc-arrow-right-st" />}
						</div>

						<div className="bc-side-st bc-right-st">
							{shotTimer(2)}
							{gameType === 'chinese_pool' && illegals(2, gameState.player2IllegalBreaks)}
							<span className="bc-name-st">{gameState.secondPlayer}</span>
						</div>
					</div>

					{title !== '' && (
						<div className="bc-subbar-st">
							<span className="bc-subbar-text-st">{title}</span>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Welsh Open yellow broadcast (Style 1) - full-bleed yellow bar with a teal frames cell
	// between two white points cells, measured from a reference broadcast frame.
	const renderYellowTvTheme = () => {
		const secondsLeft = Math.ceil(currentShotRemaining / 1000);
		const percentage = (currentShotRemaining / maxShotTime) * 100;
		const timerState = secondsLeft <= 5 ? 'timer-danger-st' : 'timer-ok-st';
		const hasFlags = game && game.has_flags !== '0';

		// country codes must be lowercase for flagcdn
		const cc1 = String(gameState.firstPlayerNationality || '').toLowerCase();
		const cc2 = String(gameState.secondPlayerNationality || '').toLowerCase();

		const isSnooker = gameType === 'snooker';

		// Possible includes the current break; Remaining does not.
		const possibleBreak = isSnooker ? gameState.currentBreakPoints + snookerPointsRemaining(gameState) : null;

		// Style 1 follows the shared Display Breaks setting like the other themes.
		const showBreak =
			displayBreaks &&
			isSnooker &&
			gameState.currentBreakPoints > 0 &&
			gameState.currentBreakPottedBalls.length > 0;

		const illegals = (player, count) => (
			<div className="ytv-illegals-st">
				{[...Array(3)].map((_, index) => (
					<span
						key={`ytv-p${player}-break-${index}`}
						className={`illegal-dot-st ${index < count ? 'illegal-on-st' : 'illegal-off-st'}`}
					/>
				))}
			</div>
		);

		const shotTimer = (player) =>
			isSnooker && gameState.shotTimerIsRunning && gameState.currentPlayer === player ? (
				<div className={`timer-pill-st ${timerState}`} style={{ '--pct': `${percentage}%` }}>
					<div className="timer-pill-fill-st" />
					<div className="timer-pill-bg-st">{secondsLeft}s</div>
				</div>
			) : null;

		return (
			<div className="streaming-root-st theme-welshopen-st theme-yellowtv-st">
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st">
						<p className="streaming-win-text-st">{gameState.winMessage}</p>
					</div>
				)}

				<div className="ytv-wrap-st">
					{renderFrameStats()}
					<div className="ytv-bar-st">
						<div className="ytv-side-st ytv-left-st">
							{hasFlags && cc1 && (
								<img className="ytv-flag-st" src={`https://flagcdn.com/w80/${cc1}.png`} alt={cc1} loading="eager" />
							)}
							<span className="ytv-name-st">{displayPlayerName(gameState.firstPlayer)}</span>
							{gameType === 'chinese_pool' && illegals(1, gameState.player1IllegalBreaks)}
							{shotTimer(1)}
						</div>

						<div className="ytv-arrow-slot-st">
							{gameState.currentPlayer === 1 && <span className="ytv-arrow-left-st" />}
						</div>

						<div className="ytv-center-st">
							<span className="ytv-sep-st" />
							{isSnooker && <div className="ytv-cell-st ytv-pts-st">{gameState.player1Score}</div>}
							{isSnooker && <span className="ytv-sep-st" />}
							<div className="ytv-cell-st ytv-frames-st">
								<span>{gameState.player1FramesWon}</span>
								<span className="ytv-race-st">{raceLabel}</span>
								<span>{gameState.player2FramesWon}</span>
							</div>
							{isSnooker && <span className="ytv-sep-st" />}
							{isSnooker && <div className="ytv-cell-st ytv-pts-st">{gameState.player2Score}</div>}
							<span className="ytv-sep-st" />
						</div>

						<div className="ytv-arrow-slot-st">
							{gameState.currentPlayer === 2 && <span className="ytv-arrow-right-st" />}
						</div>

						<div className="ytv-side-st ytv-right-st">
							{shotTimer(2)}
							{gameType === 'chinese_pool' && illegals(2, gameState.player2IllegalBreaks)}
							<span className="ytv-name-st">{displayPlayerName(gameState.secondPlayer)}</span>
							{hasFlags && cc2 && (
								<img className="ytv-flag-st" src={`https://flagcdn.com/w80/${cc2}.png`} alt={cc2} loading="eager" />
							)}
						</div>
					</div>

					{/* Hangs below the bar on the side of the player at the table, running
					    from the frame edge to exactly where the score block starts. */}
					{showBreak && (
						<div className={`ytv-break-row-st ${gameState.currentPlayer === 1 ? 'ytv-break-left-st' : 'ytv-break-right-st'}`}>
							<div className="ytv-break-st">
								<span className="ytv-break-label-st">
									Possible <span className="ytv-break-num-st">{possibleBreak}</span>
									{'  '}Break <span className="ytv-break-num-st">{gameState.currentBreakPoints}</span>
								</span>
								<span className="ytv-break-gap-st" />
								{renderBallsBreak(gameState.currentPlayer, true)}
							</div>
						</div>
					)}
				</div>
			</div>
		);
	};

	/*
	 * The selectable styles below use dedicated broadcast geometry.
	 * Style 1 uses the measured Welsh Open yellow renderer above. Style 4 uses
	 * the retained original yellow/neon renderer; Style 2 is the compact BBC rail.
	 * The other older renderers remain above as compatibility fallbacks for
	 * stored values, but these are the versions shown by the theme picker.
	 */
	const renderWstScorebarTheme = () => {
		const isSnooker = gameType === 'snooker';
		const hasFlags = game && game.has_flags !== '0';
		const cc1 = String(gameState.firstPlayerNationality || '').toLowerCase();
		const cc2 = String(gameState.secondPlayerNationality || '').toLowerCase();
		const secondsLeft = Math.ceil(currentShotRemaining / 1000);
		const title = String(streamingSettings.tournament_title || gameState.gameName || '').trim();
		const showBreak = displayBreaks && isSnooker && gameState.currentBreakPoints > 0;

		const illegalDots = (player, count) => (
			<span className="wst-illegals-st">
				{[...Array(3)].map((_, index) => (
					<i key={`wst-${player}-${index}`} className={`illegal-dot-st ${index < count ? 'illegal-on-st' : 'illegal-off-st'}`} />
				))}
			</span>
		);

		return (
			<div className="streaming-root-st theme-wst-scorebar-st">
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st"><p className="streaming-win-text-st">{gameState.winMessage}</p></div>
				)}
				<div className="wst-wrap-st">
					{renderFrameStats()}
					{showBreak && (
						<div className={`wst-break-st ${gameState.currentPlayer === 2 ? 'is-right-st' : 'is-left-st'}`}>
							<span>Break <b>{gameState.currentBreakPoints}</b></span>
							{renderBallsBreak(gameState.currentPlayer)}
						</div>
					)}
					<div className={`wst-scorebar-st ${isSnooker ? '' : 'is-pool-st'}`}>
						<div className="wst-player-st wst-player-left-st">
							{gameState.currentPlayer === 1 && <span className="wst-active-st" aria-hidden="true" />}
							<span className="wst-name-st">{displayPlayerName(gameState.firstPlayer)}</span>
							{hasFlags && cc1.length === 2 && <img className="wst-flag-st" src={`https://flagcdn.com/w40/${cc1}.png`} alt={cc1} />}
							{gameType === 'chinese_pool' && illegalDots(1, gameState.player1IllegalBreaks)}
							{isSnooker && gameState.shotTimerIsRunning && gameState.currentPlayer === 1 && <span className="wst-timer-st">{secondsLeft}</span>}
						</div>
						{isSnooker && <div className="wst-points-st">{gameState.player1Score}</div>}
						<div className="wst-match-st">
							<b>{gameState.player1FramesWon}</b><span>{raceLabel}</span><b>{gameState.player2FramesWon}</b>
						</div>
						{isSnooker && <div className="wst-points-st">{gameState.player2Score}</div>}
						<div className="wst-player-st wst-player-right-st">
							{isSnooker && gameState.shotTimerIsRunning && gameState.currentPlayer === 2 && <span className="wst-timer-st">{secondsLeft}</span>}
							{gameType === 'chinese_pool' && illegalDots(2, gameState.player2IllegalBreaks)}
							{hasFlags && cc2.length === 2 && <img className="wst-flag-st" src={`https://flagcdn.com/w40/${cc2}.png`} alt={cc2} />}
							<span className="wst-name-st">{displayPlayerName(gameState.secondPlayer)}</span>
							{gameState.currentPlayer === 2 && <span className="wst-active-st" aria-hidden="true" />}
						</div>
					</div>
					<div className="wst-event-st"><span>{title || '\u00a0'}</span></div>
				</div>
			</div>
		);
	};

	const renderUkChampionshipTheme = () => {
		const isSnooker = gameType === 'snooker';
		const secondsLeft = Math.ceil(currentShotRemaining / 1000);
		const showBreak = displayBreaks && isSnooker && gameState.currentBreakPoints > 0;

		return (
			<div className="streaming-root-st theme-uk-scorebar-st">
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st"><p className="streaming-win-text-st">{gameState.winMessage}</p></div>
				)}
				<div className="uk-wrap-st">
					{renderFrameStats()}
					{showBreak && (
						<div className={`uk-break-st ${gameState.currentPlayer === 2 ? 'is-right-st' : 'is-left-st'}`}>
							<span>BREAK <b>{gameState.currentBreakPoints}</b></span>
							{renderBallsBreak(gameState.currentPlayer)}
						</div>
					)}
					<div className={`uk-bar-st ${isSnooker ? '' : 'is-pool-st'}`}>
						<div className="uk-player-st uk-player-left-st">
							<span className="uk-name-st">{displayPlayerName(gameState.firstPlayer)}</span>
							{gameType === 'chinese_pool' && renderIllegalDots('uk', 1, gameState.player1IllegalBreaks)}
							{isSnooker && gameState.shotTimerIsRunning && gameState.currentPlayer === 1 && <span className="uk-timer-st">{secondsLeft}</span>}
							{gameState.currentPlayer === 1 && <span className="uk-arrow-left-st" aria-hidden="true" />}
						</div>
						<div className="uk-center-st">
							{isSnooker && <div className="uk-points-st">{gameState.player1Score}</div>}
							<div className="uk-frames-st"><b>{gameState.player1FramesWon}</b><span>{raceLabel}</span><b>{gameState.player2FramesWon}</b></div>
							{isSnooker && <div className="uk-points-st">{gameState.player2Score}</div>}
						</div>
						<div className="uk-player-st uk-player-right-st">
							{gameState.currentPlayer === 2 && <span className="uk-arrow-right-st" aria-hidden="true" />}
							{isSnooker && gameState.shotTimerIsRunning && gameState.currentPlayer === 2 && <span className="uk-timer-st">{secondsLeft}</span>}
							{gameType === 'chinese_pool' && renderIllegalDots('uk', 2, gameState.player2IllegalBreaks)}
							<span className="uk-name-st">{displayPlayerName(gameState.secondPlayer)}</span>
						</div>
					</div>
				</div>
			</div>
		);
	};

	const renderEnglishOpenScorebugTheme = () => {
		const isSnooker = gameType === 'snooker';
		const secondsLeft = Math.ceil(currentShotRemaining / 1000);

		return (
			<div className="streaming-root-st theme-english-open-st">
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st"><p className="streaming-win-text-st">{gameState.winMessage}</p></div>
				)}
				<div className="eo-wrap-st">
					{renderFrameStats()}
					<div className={`eo-player-st eo-player-left-st ${gameState.currentPlayer === 1 ? 'is-active-st' : ''}`}>
						{gameState.currentPlayer === 1 && <span className="eo-active-st" aria-hidden="true" />}
						<span className="eo-name-st">{displayPlayerName(gameState.firstPlayer)}</span>
						{gameType === 'chinese_pool' && renderIllegalDots('eo', 1, gameState.player1IllegalBreaks)}
						{isSnooker && gameState.shotTimerIsRunning && gameState.currentPlayer === 1 && <span className="eo-timer-st">{secondsLeft}</span>}
					</div>
					<div className={`eo-scorebug-st ${isSnooker ? '' : 'is-pool-st'}`}>
						<div className="eo-score-row-st">
							{isSnooker && (
								<svg className="eo-score-shapes-st" viewBox="0 0 244 30" preserveAspectRatio="none" aria-hidden="true">
									<defs>
										<linearGradient id="eo-red-gradient-st" x1="0" y1="0" x2="0" y2="1">
											<stop offset="0%" stopColor="#df3640" />
											<stop offset="100%" stopColor="#db3039" />
										</linearGradient>
									</defs>
									<path className="eo-white-shape-st" d="M2 0 H67 Q69 0 68 2 L54 28 Q53 30 51 30 H2 Q0 30 0 28 V2 Q0 0 2 0 Z" />
									<path className="eo-red-shape-st" d="M75 0 H169 Q171 0 172 2 L186 28 Q187 30 185 30 H59 Q57 30 58 28 L72 2 Q73 0 75 0 Z" />
									<path className="eo-white-shape-st" d="M177 0 H242 Q244 0 244 2 V28 Q244 30 242 30 H193 Q191 30 190 28 L176 2 Q175 0 177 0 Z" />
								</svg>
							)}
							{isSnooker && <div className="eo-points-st eo-points-left-st">{gameState.player1Score}</div>}
							<div className="eo-frames-st"><b>{gameState.player1FramesWon}</b><span>{raceLabel}</span><b>{gameState.player2FramesWon}</b></div>
							{isSnooker && <div className="eo-points-st eo-points-right-st">{gameState.player2Score}</div>}
						</div>
					</div>
					<div className={`eo-player-st eo-player-right-st ${gameState.currentPlayer === 2 ? 'is-active-st' : ''}`}>
						{isSnooker && gameState.shotTimerIsRunning && gameState.currentPlayer === 2 && <span className="eo-timer-st">{secondsLeft}</span>}
						{gameType === 'chinese_pool' && renderIllegalDots('eo', 2, gameState.player2IllegalBreaks)}
						<span className="eo-name-st">{displayPlayerName(gameState.secondPlayer)}</span>
						{gameState.currentPlayer === 2 && <span className="eo-active-st" aria-hidden="true" />}
					</div>
				</div>
			</div>
		);
	};

	// Saudi Arabia Masters 2025 — a full-width black score rail with emerald point
	// cells, an angled ivory match cell and a separate ivory break ribbon. Flags
	// sit outside the names, exactly as they do in the reference package.
	const renderSaudiMastersTheme = () => {
		const isSnooker = gameType === 'snooker';
		const hasFlags = game && game.has_flags !== '0';
		const cc1 = String(gameState.firstPlayerNationality || '').toLowerCase();
		const cc2 = String(gameState.secondPlayerNationality || '').toLowerCase();
		const showBreak =
			displayBreaks &&
			isSnooker &&
			gameState.currentBreakPoints > 0;

		return (
			<div className="streaming-root-st theme-saudi-masters-st">
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st"><p className="streaming-win-text-st">{gameState.winMessage}</p></div>
				)}
				<div className={`sam-wrap-st ${showBreak ? 'has-break-st' : ''}`}>
					{renderFrameStats()}
					<div className={`sam-bar-st ${isSnooker ? '' : 'is-pool-st'}`}>
						<div className="sam-player-st sam-player-left-st">
							{hasFlags && cc1.length === 2 && <img className="sam-flag-st" src={`https://flagcdn.com/w40/${cc1}.png`} alt={cc1} />}
							<span className="sam-name-st">{displayPlayerName(gameState.firstPlayer)}</span>
							{gameType === 'chinese_pool' && renderIllegalDots('sam', 1, gameState.player1IllegalBreaks)}
						</div>
						<div className="sam-arrow-slot-st">
							{gameState.currentPlayer === 1 && <span className="sam-arrow-st is-left-st" aria-hidden="true" />}
						</div>
						<div className="sam-center-st">
							{isSnooker && <div className="sam-points-st">{gameState.player1Score}</div>}
							<div className="sam-frames-st">
								<b>{gameState.player1FramesWon}</b><span>{raceLabel}</span><b>{gameState.player2FramesWon}</b>
							</div>
							{isSnooker && <div className="sam-points-st">{gameState.player2Score}</div>}
						</div>
						<div className="sam-arrow-slot-st">
							{gameState.currentPlayer === 2 && <span className="sam-arrow-st is-right-st" aria-hidden="true" />}
						</div>
						<div className="sam-player-st sam-player-right-st">
							{gameType === 'chinese_pool' && renderIllegalDots('sam', 2, gameState.player2IllegalBreaks)}
							<span className="sam-name-st">{displayPlayerName(gameState.secondPlayer)}</span>
							{hasFlags && cc2.length === 2 && <img className="sam-flag-st" src={`https://flagcdn.com/w40/${cc2}.png`} alt={cc2} />}
						</div>
					</div>

					{showBreak && (
						<div className={`sam-break-row-st ${gameState.currentPlayer === 2 ? 'is-right-st' : 'is-left-st'}`}>
							<div className="sam-break-st">
								<span className="sam-break-label-st">Break <b>{gameState.currentBreakPoints}</b></span>
								{renderBallsBreak(gameState.currentPlayer)}
							</div>
						</div>
					)}
				</div>
			</div>
		);
	};

	// Yushan World Open 2026 — the narrow navy WST package seen in the supplied
	// final. The match numerals are gold and the active player's lower rail keeps
	// the ball breakdown beside the centre block with the break total at the edge.
	const renderYushanOpenTheme = () => {
		const isSnooker = gameType === 'snooker';
		const showBreak =
			displayBreaks &&
			isSnooker &&
			gameState.currentBreakPoints > 0;

		return (
			<div className="streaming-root-st theme-yushan-open-st">
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st"><p className="streaming-win-text-st">{gameState.winMessage}</p></div>
				)}
				<div className="yuo-wrap-st">
					{renderFrameStats()}
					<div className={`yuo-bar-st ${isSnooker ? '' : 'is-pool-st'}`}>
						<div className="yuo-player-st yuo-player-left-st">
							<span className="yuo-name-st">{displayPlayerName(gameState.firstPlayer)}</span>
							{gameType === 'chinese_pool' && renderIllegalDots('yuo', 1, gameState.player1IllegalBreaks)}
							{gameState.currentPlayer === 1 && <span className="yuo-active-st is-left-st" aria-hidden="true" />}
						</div>
						{isSnooker && <div className="yuo-points-st">{gameState.player1Score}</div>}
						<div className="yuo-frames-st">
							<b>{gameState.player1FramesWon}</b><span>{raceLabel}</span><b>{gameState.player2FramesWon}</b>
						</div>
						{isSnooker && <div className="yuo-points-st">{gameState.player2Score}</div>}
						<div className="yuo-player-st yuo-player-right-st">
							{gameState.currentPlayer === 2 && <span className="yuo-active-st is-right-st" aria-hidden="true" />}
							{gameType === 'chinese_pool' && renderIllegalDots('yuo', 2, gameState.player2IllegalBreaks)}
							<span className="yuo-name-st">{displayPlayerName(gameState.secondPlayer)}</span>
						</div>
					</div>

					{showBreak && (
						<div className={`yuo-break-row-st ${gameState.currentPlayer === 2 ? 'is-right-st' : 'is-left-st'}`}>
							<div className="yuo-break-st">
								<span className="yuo-break-label-st"><b>{gameState.currentBreakPoints}</b> Break</span>
								<span className="yuo-break-spacer-st" />
								{renderBallsBreak(gameState.currentPlayer)}
							</div>
						</div>
					)}
				</div>
			</div>
		);
	};

	// World Grand Prix 2024 — black name panels flank a layered blue score block;
	// the break graphic is a short dark extension beneath the active name panel.
	const renderWorldGrandPrixTheme = () => {
		const isSnooker = gameType === 'snooker';
		const hasFlags = game && game.has_flags !== '0';
		const cc1 = String(gameState.firstPlayerNationality || '').toLowerCase();
		const cc2 = String(gameState.secondPlayerNationality || '').toLowerCase();
		const showBreak =
			displayBreaks &&
			isSnooker &&
			gameState.currentBreakPoints > 0;

		return (
			<div className="streaming-root-st theme-world-grand-prix-st">
				{gameState.isGameFinished && gameState.winMessage && (
					<div className="streaming-overlay-st"><p className="streaming-win-text-st">{gameState.winMessage}</p></div>
				)}
				<div className="wgp-wrap-st">
					{renderFrameStats()}
					<div className={`wgp-bar-st ${isSnooker ? '' : 'is-pool-st'}`}>
						<div className="wgp-player-st wgp-player-left-st">
							{hasFlags && cc1.length === 2 && <img className="wgp-flag-st" src={`https://flagcdn.com/w40/${cc1}.png`} alt={cc1} />}
							<span className="wgp-name-st">{displayPlayerName(gameState.firstPlayer)}</span>
							{gameType === 'chinese_pool' && renderIllegalDots('wgp', 1, gameState.player1IllegalBreaks)}
							{gameState.currentPlayer === 1 && <span className="wgp-active-st is-left-st" aria-hidden="true" />}
						</div>
						{isSnooker && <div className="wgp-points-st">{gameState.player1Score}</div>}
						<div className="wgp-frames-st">
							<b>{gameState.player1FramesWon}</b><span>{raceLabel}</span><b>{gameState.player2FramesWon}</b>
						</div>
						{isSnooker && <div className="wgp-points-st">{gameState.player2Score}</div>}
						<div className="wgp-player-st wgp-player-right-st">
							{gameState.currentPlayer === 2 && <span className="wgp-active-st is-right-st" aria-hidden="true" />}
							{gameType === 'chinese_pool' && renderIllegalDots('wgp', 2, gameState.player2IllegalBreaks)}
							<span className="wgp-name-st">{displayPlayerName(gameState.secondPlayer)}</span>
							{hasFlags && cc2.length === 2 && <img className="wgp-flag-st" src={`https://flagcdn.com/w40/${cc2}.png`} alt={cc2} />}
						</div>
					</div>

					{showBreak && (
						<div className={`wgp-break-st ${gameState.currentPlayer === 2 ? 'is-right-st' : 'is-left-st'}`}>
							<span className="wgp-break-label-st">Break <b>{gameState.currentBreakPoints}</b></span>
							{renderBallsBreak(gameState.currentPlayer)}
						</div>
					)}
				</div>
			</div>
		);
	};

	const renderTheme = () => {
		const theme = streamingSettings.streaming_theme || 'welshopen';
		switch (theme) {
			case 'default':
				// Migrate old saved values to the current Style 1 (Welsh Open).
				return renderYellowTvTheme();
			case 'modern':
				return renderModernTheme();
			case 'neon':
				return renderUkChampionshipTheme();
			case 'welshopen':
				return renderYellowTvTheme();
			case 'broadcast':
				return renderEnglishOpenScorebugTheme();
			case 'classic-neon':
				return renderNeonTheme();
			case 'saudi-masters':
				return renderSaudiMastersTheme();
			case 'yushan-open':
				return renderYushanOpenTheme();
			case 'world-grand-prix':
				return renderWorldGrandPrixTheme();
			case 'yellowtv':
				return renderYellowTvTheme();
			case 'legacy-default':
				return renderDefaultTheme();
			case 'legacy-neon':
				return renderNeonTheme();
			case 'legacy-welshopen':
				return renderWelshOpenTheme();
			case 'legacy-broadcast':
				return renderBroadcastTheme();
			default:
				return renderWstScorebarTheme();
		}
	};

	if (isInitializing) {
		return (
			<div className="loading-root-st">
				<div className="loading-box-st">
					<div className="loading-spinner-st" />
					<h3 className="loading-title-st">Loading Stream...</h3>
				</div>
			</div>
		);
	}

	const overlay = renderTheme();
	const playerNameSize = normalizePlayerNameSize(streamingSettings.player_name_size);
	return React.cloneElement(overlay, {
		className: `${overlay.props.className}${showStats ? ' has-streaming-stats-st' : ''}`,
		'data-player-name-size': playerNameSize,
		style: { ...overlay.props.style, '--st-name-scale': playerNameSize / 100 },
	});
};

export default Streaming;
